import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface Client {
  ID: number;
  ClientName: string;
  // Add other client properties as needed
}

export interface RecentClient {
  client: Client;
  lastAccessed: Date;
  accessCount: number;
}

interface ClientState {
  // Current selected client
  selectedClient: Client | null;

  // Client list view preferences
  viewMode: 'grid' | 'list' | 'compact';

  // Search and filter preferences
  searchTerm: string;
  sortBy: 'name' | 'id' | 'date';
  sortOrder: 'asc' | 'desc';

  // UI preferences
  showClientDetails: boolean;
  autoRefresh: boolean;

  // Recent clients for quick access with timestamps
  recentClients: RecentClient[];

  // Favorite clients
  favoriteClients: number[]; // Array of client IDs
}

interface ClientActions {
  // Client selection
  selectClient: (client: Client | null) => void;

  /**
   * Select a client by ID from a provided client list
   * @param clientId - The ID of the client to select
   * @param allClients - Array of available clients to search from
   */
  selectClientById: (clientId: number, allClients?: Client[]) => void;

  /**
   * Open a client by ID (async version for future API integration)
   * @param clientId - The ID of the client to open
   * @param allClients - Array of available clients to search from
   * @returns Promise resolving to the found client or null
   */
  openClientById: (
    clientId: number,
    allClients?: Client[],
  ) => Promise<Client | null>;

  /**
   * Get the currently selected client
   * @returns The currently selected client or null
   */
  getSelectedClient: () => Client | null;

  /**
   * Clear the currently selected client
   */
  clearSelectedClient: () => void;

  // View preferences
  setViewMode: (mode: 'grid' | 'list' | 'compact') => void;

  // Search and filter
  setSearchTerm: (term: string) => void;
  setSortBy: (sortBy: 'name' | 'id' | 'date') => void;
  setSortOrder: (order: 'asc' | 'desc') => void;

  // UI preferences
  toggleClientDetails: () => void;
  setShowClientDetails: (show: boolean) => void;
  toggleAutoRefresh: () => void;

  // Recent clients management
  addToRecent: (client: Client) => void;
  removeFromRecent: (clientId: number) => void;
  clearRecent: () => void;
  getRecentClients: () => RecentClient[];
  getRecentClientsByTime: (hours?: number) => RecentClient[];
  getMostAccessedClients: (limit?: number) => RecentClient[];

  // Favorite clients management
  toggleFavorite: (clientId: number) => void;
  addToFavorites: (clientId: number) => void;
  removeFromFavorites: (clientId: number) => void;
  clearFavorites: () => void;

  // Utility actions
  resetPreferences: () => void;

  /**
   * Clean up invalid entries from recent clients array
   */
  cleanupRecentClients: () => void;

  // Client analytics and insights
  getClientAccessStats: () => {
    totalAccesses: number;
    uniqueClients: number;
    averageAccessesPerClient: number;
    mostActiveDay: string;
  };
  getClientActivityTimeline: (days?: number) => {
    date: string;
    accessCount: number;
    uniqueClients: number;
  }[];
}

export type ClientStore = ClientState & ClientActions;

const initialState: ClientState = {
  selectedClient: null,
  viewMode: 'list',
  searchTerm: '',
  sortBy: 'name',
  sortOrder: 'asc',
  showClientDetails: true,
  autoRefresh: false,
  recentClients: [],
  favoriteClients: [],
};

export const useClientStore = create<ClientStore>()(
  persist(
    (set, get) => ({
      ...initialState,

      // Client selection
      selectClient: (client) => {
        set({ selectedClient: client });
        if (client && client.ID) {
          get().addToRecent(client);
        }
      },

      selectClientById: (clientId, allClients = []) => {
        const client = allClients.find((c) => c.ID === clientId);
        if (client) {
          get().selectClient(client);
        } else {
          console.warn(
            `Client with ID ${clientId} not found in provided client list`,
          );
        }
      },

      openClientById: async (clientId, allClients = []) => {
        const client = allClients.find((c) => c.ID === clientId);
        if (client) {
          get().selectClient(client);
          return client;
        } else {
          console.warn(`Client with ID ${clientId} not found`);
          return null;
        }
      },

      getSelectedClient: () => {
        const state = get();
        return state.selectedClient;
      },

      clearSelectedClient: () => {
        set({ selectedClient: null });
      },

      // View preferences
      setViewMode: (mode) => set({ viewMode: mode }),

      // Search and filter
      setSearchTerm: (term) => set({ searchTerm: term }),
      setSortBy: (sortBy) => set({ sortBy }),
      setSortOrder: (order) => set({ sortOrder: order }),

      // UI preferences
      toggleClientDetails: () =>
        set((state) => ({ showClientDetails: !state.showClientDetails })),
      setShowClientDetails: (show) => set({ showClientDetails: show }),
      toggleAutoRefresh: () =>
        set((state) => ({ autoRefresh: !state.autoRefresh })),

      // Recent clients management
      addToRecent: (client) => {
        // Early return if client is invalid
        if (!client || !client.ID) {
          console.warn('Invalid client provided to addToRecent:', client);
          return;
        }

        set((state) => {
          // Filter out any invalid recent clients first
          const validRecentClients = state.recentClients.filter(
            (rc) => rc && rc.client && rc.client.ID,
          );

          const existingIndex = validRecentClients.findIndex(
            (rc) => rc.client.ID === client.ID,
          );

          let newRecent = [...validRecentClients];

          if (existingIndex !== -1) {
            // Update existing client with new timestamp and increment count
            const existing = newRecent[existingIndex];
            newRecent[existingIndex] = {
              client: existing.client,
              lastAccessed: new Date(),
              accessCount: existing.accessCount + 1,
            };
            // Move to front
            const updatedClient = newRecent[existingIndex];
            newRecent.splice(existingIndex, 1);
            newRecent.unshift(updatedClient);
          } else {
            // Add new client to front
            const newRecentClient: RecentClient = {
              client,
              lastAccessed: new Date(),
              accessCount: 1,
            };
            newRecent.unshift(newRecentClient);
          }

          // Limit to 20 items
          newRecent = newRecent.slice(0, 20);

          return { recentClients: newRecent };
        });
      },

      removeFromRecent: (clientId) => {
        set((state) => ({
          recentClients: state.recentClients.filter(
            (rc) => rc && rc.client && rc.client.ID !== clientId,
          ),
        }));
      },

      clearRecent: () => set({ recentClients: [] }),

      // Get recent clients with additional functionality
      getRecentClients: () => {
        const state = get();
        // Filter out invalid entries
        return state.recentClients.filter(
          (rc) => rc && rc.client && rc.client.ID,
        );
      },

      getRecentClientsByTime: (hours = 24) => {
        const state = get();
        const cutoffTime = new Date(Date.now() - hours * 60 * 60 * 1000);
        return state.recentClients.filter(
          (rc) =>
            rc &&
            rc.client &&
            rc.client.ID &&
            rc.lastAccessed &&
            rc.lastAccessed > cutoffTime,
        );
      },

      getMostAccessedClients: (limit = 10) => {
        const state = get();
        return state.recentClients
          .filter((rc) => rc && rc.client && rc.client.ID && rc.accessCount)
          .sort((a, b) => b.accessCount - a.accessCount)
          .slice(0, limit);
      },

      // Favorite clients management
      toggleFavorite: (clientId) => {
        set((state) => {
          const isFavorite = state.favoriteClients.includes(clientId);
          if (isFavorite) {
            return {
              favoriteClients: state.favoriteClients.filter(
                (id) => id !== clientId,
              ),
            };
          } else {
            return {
              favoriteClients: [...state.favoriteClients, clientId],
            };
          }
        });
      },

      addToFavorites: (clientId) => {
        set((state) => ({
          favoriteClients: state.favoriteClients.includes(clientId)
            ? state.favoriteClients
            : [...state.favoriteClients, clientId],
        }));
      },

      removeFromFavorites: (clientId) => {
        set((state) => ({
          favoriteClients: state.favoriteClients.filter(
            (id) => id !== clientId,
          ),
        }));
      },

      clearFavorites: () => set({ favoriteClients: [] }),

      // Utility actions
      resetPreferences: () => set(initialState),

      /**
       * Clean up invalid entries from recent clients array
       */
      cleanupRecentClients: () => {
        set((state) => ({
          recentClients: state.recentClients.filter(
            (rc) => rc && rc.client && rc.client.ID,
          ),
        }));
      },

      // Client analytics and insights
      getClientAccessStats: () => {
        const state = get();
        // Filter valid recent clients
        const validRecentClients = state.recentClients.filter(
          (rc) => rc && rc.client && rc.client.ID && rc.accessCount,
        );

        const totalAccesses = validRecentClients.reduce(
          (sum, rc) => sum + rc.accessCount,
          0,
        );
        const uniqueClients = validRecentClients.length;
        const averageAccessesPerClient =
          uniqueClients > 0 ? totalAccesses / uniqueClients : 0;

        // Find most active day
        const dayCounts = new Map<string, number>();
        validRecentClients.forEach((rc) => {
          if (rc.lastAccessed) {
            const day = rc.lastAccessed.toDateString();
            dayCounts.set(day, (dayCounts.get(day) || 0) + rc.accessCount);
          }
        });

        const mostActiveDay =
          Array.from(dayCounts.entries()).sort(
            ([, a], [, b]) => b - a,
          )[0]?.[0] || 'No activity';

        return {
          totalAccesses,
          uniqueClients,
          averageAccessesPerClient:
            Math.round(averageAccessesPerClient * 100) / 100,
          mostActiveDay,
        };
      },

      getClientActivityTimeline: (days = 7) => {
        const state = get();
        const timeline = new Map<
          string,
          { accessCount: number; uniqueClients: Set<number> }
        >();

        // Initialize timeline for the last N days
        for (let i = days - 1; i >= 0; i--) {
          const date = new Date(Date.now() - i * 24 * 60 * 60 * 1000);
          const dateStr = date.toDateString();
          timeline.set(dateStr, { accessCount: 0, uniqueClients: new Set() });
        }

        // Populate timeline with actual data from valid recent clients
        state.recentClients
          .filter(
            (rc) =>
              rc &&
              rc.client &&
              rc.client.ID &&
              rc.lastAccessed &&
              rc.accessCount,
          )
          .forEach((rc) => {
            const dateStr = rc.lastAccessed.toDateString();
            const existing = timeline.get(dateStr);
            if (existing) {
              existing.accessCount += rc.accessCount;
              existing.uniqueClients.add(rc.client.ID);
            }
          });

        return Array.from(timeline.entries()).map(([date, data]) => ({
          date,
          accessCount: data.accessCount,
          uniqueClients: data.uniqueClients.size,
        }));
      },
    }),
    {
      name: 'client-store',
      partialize: (state) => ({
        viewMode: state.viewMode,
        sortBy: state.sortBy,
        sortOrder: state.sortOrder,
        showClientDetails: state.showClientDetails,
        autoRefresh: state.autoRefresh,
        recentClients: state.recentClients,
        favoriteClients: state.favoriteClients,
      }),
    },
  ),
);
